import {
  specialCharactersMessaging,
  BASICINPUTSCHEMA,
  getCurrentProductFromUrl,
  getCurrentProductFilter,
  getFilterTypeFromPathname,
  getLessonsLink,
  sortCurriculumTypesWithKFirst,
  createSchema,
  BaseValidator,
  getSchoolOptionFromCurrentSite,
  getInitialSelectedSchool,
  checkBlockingValidations as sharedCheckBlockingValidations,
  checkLimitReached as sharedCheckLimitReached,
} from 'experienceComponents/CreateInstanceShared/dataUtils.js'

import {
  TICourseNameToProgramMap,
  k8V2ContentfulMappings,
} from 'utils/productMaps.js'
import { getCourseInstancePath } from 'components/dashboard/ClassCard/helpers.js'

// Grade display mapping for user-friendly labels
const GRADE_DISPLAY_MAP = {
  'k-2_curriculum_tier2': 'Grade K-2',
  '3-5_curriculum_tier2': 'Grade 3-5',
  '6-8_curriculum_tier2': 'Grade 6-8',
}

// Function to convert internal grade values to display values
export const convertGradeToDisplayValue = gradeValue => {
  return GRADE_DISPLAY_MAP[gradeValue] || gradeValue
}

// Course filtering function for small groups
export const getRelevantCourseListForSmallGroup = (courses = []) => {
  // For small groups, filter out courses that don't have "tier2" in their name
  return courses.filter(course => course.name && course.name.includes('tier2'))
}

// Filter small group curriculum types
export const filterSmallGroupCurriculumTypes = (grades = []) => {
  const validCurriculumTypes = [
    '6-8_curriculum_tier2',
    '3-5_curriculum_tier2',
    'k-2_curriculum_tier2',
  ]
  return grades.filter(grade => validCurriculumTypes.includes(grade))
}

// Convert display name to curriculum type
export const convertGradeNameToLabel = gradeName => {
  const displayToCurriculumMap = {
    'Grade K-2': 'k-2_curriculum_tier2',
    'Grade 3-5': '3-5_curriculum_tier2',
    'Grade 6-8': '6-8_curriculum_tier2',
  }
  return displayToCurriculumMap[gradeName] || gradeName
}

// Get program name from selected grade
export const getProgramName = selectedGrade => {
  return TICourseNameToProgramMap[selectedGrade]
}

// Create view class behavior
export const createViewClassBehavior = (state, props) => {
  const { redirectToMyClasses, pathName, currentSite } = props || {}
  const { selectedCourse, selectedGrade, selectedSchool } = state || {}
  const { id: courseInstanceId } = selectedCourse || {}

  // If we have the newly created instance id, build a direct route to it
  if (courseInstanceId && redirectToMyClasses) {
    try {
      // Determine product slug from URL (e.g., 'elementary' | 'middle-school')
      const currentProduct = getCurrentProductFromUrl(pathName)
      const programSlug = currentProduct?.productName

      // Use the display grade from state (e.g., 'Grade K-2', 'Grade 6-8')
      const displayGrade = selectedGrade

      // Convert display grade to internal label to look up defaultUnit
      const internalGrade = convertGradeNameToLabel(displayGrade)
      const mapping = Array.isArray(k8V2ContentfulMappings)
        ? k8V2ContentfulMappings.find(m => m.courseName === internalGrade)
        : null
      const defaultUnit = mapping?.defaultUnit || ''

      // Preferred: Build the tier-2 course instance path
      if (programSlug && displayGrade) {
        let path = getCourseInstancePath({
          program: programSlug,
          gradeDisplayTitle: displayGrade,
          instance: courseInstanceId,
          tier: 2,
          defaultUnit,
        })

        // Append site param if available
        const siteId = selectedSchool?.siteId || currentSite?.siteId
        if (siteId) {
          path = `${path}&site=${siteId}`
        }
        redirectToMyClasses(path)
      } else if (programSlug) {
        // Fallback: generic teach route still binds the instance via class query
        const siteId = selectedSchool?.siteId || currentSite?.siteId
        let fallbackPath = `/product/${programSlug}/lessons/teach/?class=${courseInstanceId}`
        if (siteId) {
          fallbackPath = `${fallbackPath}&site=${siteId}`
        }
        redirectToMyClasses(fallbackPath)
      } else {
        // Last resort: default
        redirectToMyClasses()
      }
    } catch (e) {
      // On any error, fallback to default redirect
      redirectToMyClasses()
    }
  }

  return { newState: { ...state, modalContent: null } }
}

// Create component-specific schema
export const CREATESMALLGROUPSCHEMA = createSchema({
  schemaName: 'CREATESMALLGROUPSCHEMA',
  requiresLicense: false,
  requiresDates: false,
  filterFunction: 'filterSmallGroupCurriculumTypes',
  sortFunction: 'sortCurriculumTypesWithKFirst',
  duplicateMessage: 'You may not have a duplicate small group name',
  validatorClass: 'CreateSmallGroupValidator',
  validationType: 'smallGroup',
})

// Create component-specific validator class
export class CreateSmallGroupValidator extends BaseValidator {
  constructor(state, props) {
    super(state, props, {
      schemaName: 'CREATESMALLGROUPSCHEMA',
      requiresLicense: false,
      requiresDates: false,
      filterFunction: 'filterSmallGroupCurriculumTypes',
      sortFunction: 'sortCurriculumTypesWithKFirst',
      duplicateMessage: 'You may not have a duplicate small group name',
      validatorClass: 'CreateSmallGroupValidator',
      validationType: 'smallGroup',
    })
  }
}

// Re-export shared functions for backward compatibility
export {
  specialCharactersMessaging,
  BASICINPUTSCHEMA,
  getCurrentProductFromUrl,
  getCurrentProductFilter,
  getFilterTypeFromPathname,
  getLessonsLink,
  sortCurriculumTypesWithKFirst,
  getSchoolOptionFromCurrentSite,
  getInitialSelectedSchool,
}

// Component-specific wrapper functions
export const checkBlockingValidations = async (state, props) => {
  return sharedCheckBlockingValidations(state, props, {
    schemaName: 'CREATESMALLGROUPSCHEMA',
    requiresLicense: false,
    requiresDates: false,
    filterFunction: 'filterSmallGroupCurriculumTypes',
    sortFunction: 'sortCurriculumTypesWithKFirst',
    duplicateMessage: 'You may not have a duplicate small group name',
    validatorClass: 'CreateSmallGroupValidator',
    validationType: 'smallGroup',
  })
}

export const checkLimitReached = (pathName, lms, maxClasses) => {
  return sharedCheckLimitReached(pathName, lms, maxClasses)
}
